import { useState } from 'react'
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  RefreshCw,
  Trash2,
  ArrowRight,
  RotateCcw,
} from 'lucide-react'
import { clsx } from 'clsx'
import { Link } from 'react-router-dom'
import { useJobHistory, useDeleteJob, type JobListItem } from '../hooks/useIngestion'

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string; icon: React.ElementType }> = {
    completed:     { label: 'Completato',   cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',  icon: CheckCircle2 },
    failed:        { label: 'Fallito',      cls: 'bg-red-500/10    text-red-400    border-red-500/20',        icon: AlertCircle },
    mapping:       { label: 'Mapping',      cls: 'bg-amber-500/10  text-amber-400  border-amber-500/20',      icon: AlertCircle },
    pending:       { label: 'In attesa',    cls: 'bg-[--km-surface-3] text-[--km-text-muted] border-[--km-border]', icon: Clock },
    parsing:       { label: 'Analisi',      cls: 'bg-[--km-primary]/10 text-[--km-primary] border-[--km-primary]/20', icon: Loader2 },
    validating:    { label: 'Validazione',  cls: 'bg-[--km-primary]/10 text-[--km-primary] border-[--km-primary]/20', icon: Loader2 },
    storing:       { label: 'Salvataggio',  cls: 'bg-[--km-primary]/10 text-[--km-primary] border-[--km-primary]/20', icon: Loader2 },
    kpi_computing: { label: 'Calcolo KPI',  cls: 'bg-[--km-primary]/10 text-[--km-primary] border-[--km-primary]/20', icon: Loader2 },
  }
  const cfg = map[status] ?? { label: status, cls: 'bg-[--km-surface-3] text-[--km-text-muted] border-[--km-border]', icon: Clock }
  const Icon = cfg.icon
  return (
    <span className={clsx('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium', cfg.cls)}>
      <Icon size={11} />
      {cfg.label}
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Row component
// ─────────────────────────────────────────────────────────────────────────────

function JobRow({ job, onDelete }: { job: JobListItem; onDelete: (id: string) => void }) {
  const [confirming, setConfirming] = useState(false)
  const isInProgress = !['completed', 'failed', 'mapping'].includes(job.status)

  return (
    <tr className="border-b border-[--km-border] transition-colors hover:bg-[--km-surface-2]">
      {/* File */}
      <td className="py-3 px-4 max-w-[200px]">
        <div className="flex items-center gap-2 min-w-0">
          <FileText size={14} className="shrink-0 text-[--km-primary]" aria-hidden="true" />
          <span className="text-sm font-medium text-[--km-text-primary] truncate" title={job.file_name ?? undefined}>
            {job.file_name ?? '—'}
          </span>
        </div>
      </td>

      {/* Stato */}
      <td className="py-3 px-4 whitespace-nowrap">
        <StatusBadge status={job.status} />
      </td>

      {/* Righe */}
      <td className="py-3 px-4 whitespace-nowrap hidden sm:table-cell">
        {job.rows_total != null ? (
          <span className="text-sm tabular-nums text-[--km-text-secondary]">
            <span className="text-emerald-400 font-medium">{(job.rows_valid ?? 0).toLocaleString('it-IT')}</span>
            {' / '}
            {job.rows_total.toLocaleString('it-IT')}
            {(job.rows_error ?? 0) > 0 && (
              <span className="text-[--km-warning] ml-1">({job.rows_error} err.)</span>
            )}
          </span>
        ) : (
          <span className="text-sm text-[--km-text-muted]">—</span>
        )}
      </td>

      {/* Data */}
      <td className="py-3 px-4 whitespace-nowrap hidden md:table-cell">
        <span className="text-sm text-[--km-text-muted]">{formatDate(job.created_at)}</span>
      </td>

      {/* Azioni */}
      <td className="py-3 px-4 whitespace-nowrap">
        <div className="flex items-center gap-2 justify-end">
          {/* Link MappingEditor per job in attesa di mapping */}
          {job.status === 'mapping' && (
            <Link
              to="/upload"
              state={{ jobId: job.job_id }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20 px-2.5 py-1 text-xs font-medium text-amber-400 transition-colors hover:bg-amber-500/20"
              title="Vai al mapping colonne"
            >
              Mappa colonne
              <ArrowRight size={11} />
            </Link>
          )}

          {/* Ricaricare — apre la pagina upload */}
          {job.status === 'failed' && (
            <Link
              to="/upload"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[--km-border] px-2.5 py-1 text-xs text-[--km-text-secondary] transition-colors hover:border-[--km-border-hover] hover:text-[--km-text-primary]"
              title="Ricarica file"
            >
              <RotateCcw size={11} />
              Ricarica
            </Link>
          )}

          {/* Elimina — non disponibile se il job è ancora in corso */}
          {!isInProgress && (
            confirming ? (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => onDelete(job.job_id)}
                  className="rounded-lg bg-red-500/10 border border-red-500/20 px-2.5 py-1 text-xs font-medium text-red-400 transition-colors hover:bg-red-500/20"
                >
                  Conferma
                </button>
                <button
                  onClick={() => setConfirming(false)}
                  className="rounded-lg border border-[--km-border] px-2.5 py-1 text-xs text-[--km-text-muted] transition-colors hover:text-[--km-text-primary]"
                >
                  Annulla
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirming(true)}
                className="p-1.5 rounded-lg text-[--km-text-muted] transition-colors hover:text-red-400 hover:bg-red-500/10"
                aria-label="Elimina job"
                title="Elimina"
              >
                <Trash2 size={14} />
              </button>
            )
          )}
        </div>
      </td>
    </tr>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export function UploadedFiles() {
  const { data: jobs, isLoading, isError, refetch } = useJobHistory()
  const deleteJob = useDeleteJob()

  const handleDelete = (jobId: string) => {
    deleteJob.mutate(jobId)
  }

  return (
    <section aria-label="File caricati" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[--km-text-primary]">File caricati</h1>
          <p className="text-sm text-[--km-text-muted] mt-0.5">
            Storico degli upload con stato elaborazione, righe importate ed eventuali errori.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-lg border border-[--km-border] px-3 py-2 text-sm text-[--km-text-secondary] transition-colors hover:border-[--km-border-hover] hover:text-[--km-text-primary]"
          >
            <RefreshCw size={14} />
            Aggiorna
          </button>
          <Link
            to="/upload"
            className="inline-flex items-center gap-2 rounded-lg bg-[--km-primary] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
          >
            Nuovo upload
          </Link>
        </div>
      </div>

      {/* Tabella */}
      <div className="km-panel overflow-hidden">
        {isLoading && (
          <div className="flex items-center justify-center gap-2 py-16 text-[--km-text-muted]">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Caricamento storico…</span>
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <AlertCircle size={32} className="text-[--km-error]" />
            <p className="text-sm text-[--km-text-secondary]">Impossibile caricare lo storico dei file.</p>
            <button
              onClick={() => refetch()}
              className="text-sm text-[--km-primary] hover:underline"
            >
              Riprova
            </button>
          </div>
        )}

        {!isLoading && !isError && (!jobs || jobs.length === 0) && (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <FileText size={40} className="text-[--km-text-disabled]" />
            <div>
              <p className="text-sm font-medium text-[--km-text-primary]">Nessun file caricato</p>
              <p className="text-xs text-[--km-text-muted] mt-1">Carica il primo file per popolare la dashboard.</p>
            </div>
            <Link
              to="/upload"
              className="inline-flex items-center gap-2 rounded-lg bg-[--km-primary] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
            >
              Carica file
            </Link>
          </div>
        )}

        {!isLoading && !isError && jobs && jobs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[--km-border]">
                  <th className="py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[--km-text-disabled]">File</th>
                  <th className="py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[--km-text-disabled]">Stato</th>
                  <th className="py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[--km-text-disabled] hidden sm:table-cell">Righe</th>
                  <th className="py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[--km-text-disabled] hidden md:table-cell">Data</th>
                  <th className="py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[--km-text-disabled] text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <JobRow key={job.job_id} job={job} onDelete={handleDelete} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Nota */}
      {jobs && jobs.length > 0 && (
        <p className="text-xs text-[--km-text-disabled]">
          Eliminare un job rimuove solo la voce dallo storico — i dati già importati nelle sezioni dashboard restano invariati.
        </p>
      )}
    </section>
  )
}
